<template>
	<transition name="modal-fade">
		<div v-if="isModalVisible" class="modal-backdrop">
			<div
				class="modal"
				role="dialog"
				aria-labelledby="addModalTitle"
				aria-describedby="addModalDescription"
			>
				<header class="modal-header" id="addModalTitle">
					<div class="modal-header-content">
						<div class="modal-title-section">
							<h2 class="modal-main-title">Add Special Price</h2>
						</div>
						<div class="modal-customer-info">
							<span class="customer-id">{{ customer.customer_id }}</span>
							<span class="separator">•</span>
							<span class="customer-name">{{ customer.customer_title }}</span>
							<span v-if="customer.customer_nickname" class="customer-nickname">
								({{ customer.customer_nickname }})
							</span>
						</div>
					</div>
				</header>

				<!-- Close Button -->
				<button
					class="modal-close-absolute"
					@click="closeModal"
					aria-label="Close modal"
				>
					<span class="close-x">✕</span>
					<span class="close-text">Close</span>
				</button>

				<section class="modal-body" id="addModalDescription">
					<!-- Error Message Display -->
					<b-message
						v-if="errorMessage"
						type="is-danger"
						:closable="true"
						@close="errorMessage = null"
					>
						{{ errorMessage }}
					</b-message>

					<!-- Success Message Display -->
					<b-message
						v-if="successMessage"
						type="is-success"
						:closable="true"
						@close="successMessage = null"
					>
						{{ successMessage }}
					</b-message>

					<div class="form-container">
						<!-- Stock Selection -->
						<div class="form-group">
							<label class="form-label">Select Stock Item</label>
							<b-field>
								<b-autocomplete
									v-model="stockSearchText"
									:data="filteredStocks"
									placeholder="Search and select stock item..."
									field="displayText"
									:loading="stocksLoading"
									:disabled="!hasAvailableStocks"
									@select="onStockSelectedFromAutocomplete"
									:clearable="true"
									:open-on-focus="true"
									expanded
								>
									<template slot="empty">
										<span class="no-results">No matching stocks found</span>
									</template>
									<template slot-scope="props">
										<div class="stock-option">
											<span class="stock-id">{{ props.option.stock_id }}</span>
											<span class="stock-separator">-</span>
											<span class="stock-name">{{
												props.option.stock_name
											}}</span>
											<span class="stock-separator">-</span>
											<span class="stock-price">{{
												formatPrice(props.option.customerPrice)
											}}</span>
										</div>
									</template>
								</b-autocomplete>
							</b-field>
							<p
								v-if="!hasAvailableStocks && noStocksMessage"
								class="help is-danger"
							>
								{{ noStocksMessage }}
							</p>
						</div>

						<!-- Current Price Display -->
						<div class="form-group">
							<label class="form-label">Current Customer Class Price</label>
							<div class="current-price-display">
								<span v-if="selectedStock" class="price-label">
									Class {{ customerPriceClass }}:
								</span>
								{{
									selectedStock ? formatPrice(selectedStock.customerPrice) : '-'
								}}
							</div>
						</div>

						<!-- Special Price Input -->
						<div class="form-group">
							<label class="form-label">Special Price</label>
							<b-field>
								<b-input
									v-model="specialPrice"
									type="number"
									placeholder="Enter special price"
									min="0"
									step="1"
									:has-counter="false"
									:disabled="!selectedStock"
									expanded
								/>
							</b-field>
						</div>

						<!-- Action Buttons -->
						<div class="form-actions">
							<button
								class="btn-save"
								@click="saveSpecialPrice"
								:disabled="isSaving || !selectedStock || !isValidPrice"
							>
								<svg
									v-if="!isSaving"
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<polyline points="20 6 9 17 4 12"></polyline>
								</svg>
								<span v-if="!isSaving">Save Special Price</span>
								<span v-else>Saving...</span>
							</button>
							<button class="btn-cancel" @click="resetForm">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
								<span>Clear</span>
							</button>
						</div>
					</div>
				</section>
			</div>
		</div>
	</transition>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import { useStockItemsWithCustomerPrice } from '../composables/useStockItemsWithCustomerPrice'
import store from '@/store'

export default {
	name: 'AddSpecialPriceModal',
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		customer: {
			type: Object,
			default: () => ({}),
		},
		existingPrices: {
			type: Array,
			default: () => [],
		},
	},
	setup(props, { emit }) {
		const isModalVisible = ref(false)
		const selectedStock = ref(null)
		const specialPrice = ref('')
		const isSaving = ref(false)
		const errorMessage = ref(null)
		const successMessage = ref(null)
		const stockSearchText = ref('')

		// Get company ID from store
		const companyId = computed(() => store.getters.getUserCurrentCompany)

		// Use the composable to get formatted stock options
		const {
			formattedStockOptions,
			loading: stocksLoading,
			error: stocksError,
			formatPrice,
			hasAvailableStocks,
			noStocksMessage,
			customerPriceClass,
			refreshStocks,
		} = useStockItemsWithCustomerPrice(
			companyId,
			computed(() => props.customer.customer_id),
			computed(() => props.existingPrices),
		)

		// Watch for visibility changes
		watch(
			() => props.visible,
			(newVal) => {
				isModalVisible.value = newVal
				if (newVal) {
					// Refresh stock items when modal opens
					refreshStocks()
				}
			},
		)

		// Watch for stock loading errors
		watch(stocksError, (error) => {
			if (error) {
				errorMessage.value = 'Failed to load stock items. Please try again.'
				console.error('Error loading stock items:', error)
			}
		})

		// Computed property to validate price
		const isValidPrice = computed(() => {
			const price = parseInt(specialPrice.value)
			return !isNaN(price) && price >= 0
		})

		// Computed property for filtered stocks based on search text
		const filteredStocks = computed(() => {
			if (!stockSearchText.value) {
				return formattedStockOptions.value
			}

			const searchTerm = stockSearchText.value.toLowerCase()
			return formattedStockOptions.value.filter((stock) => {
				return (
					stock.stock_id.toLowerCase().includes(searchTerm) ||
					stock.stock_name.toLowerCase().includes(searchTerm) ||
					stock.displayText.toLowerCase().includes(searchTerm)
				)
			})
		})

		// Handle stock selection from autocomplete
		const onStockSelectedFromAutocomplete = (option) => {
			if (option) {
				selectedStock.value = option
				specialPrice.value = ''
				errorMessage.value = null
			}
		}

		// Save special price
		const saveSpecialPrice = async () => {
			if (!isValidPrice.value || !selectedStock.value) {
				errorMessage.value = 'Please enter a valid price'
				return
			}

			isSaving.value = true
			errorMessage.value = null

			try {
				const priceData = {
					customer_id: props.customer.customer_id,
					stock_id: selectedStock.value.stock_id,
					stock_name: selectedStock.value.stock_name,
					customers_price: parseInt(specialPrice.value),
				}

				// Emit event to parent to handle the save
				emit('save-price', priceData)

				// Show success and reset
				successMessage.value = 'Special price added successfully'
				setTimeout(() => {
					resetForm()
					successMessage.value = null
				}, 1500)
			} catch (error) {
				errorMessage.value = 'Failed to save special price. Please try again.'
				console.error('Error saving special price:', error)
			} finally {
				isSaving.value = false
			}
		}

		// Reset form
		const resetForm = () => {
			selectedStock.value = null
			specialPrice.value = ''
			stockSearchText.value = ''
			errorMessage.value = null
		}

		// Close modal
		const closeModal = () => {
			resetForm()
			successMessage.value = null
			emit('close')
		}

		return {
			isModalVisible,
			selectedStock,
			specialPrice,
			isSaving,
			errorMessage,
			successMessage,
			stocksLoading,
			stockOptions: formattedStockOptions,
			isValidPrice,
			formatPrice,
			hasAvailableStocks,
			noStocksMessage,
			customerPriceClass,
			stockSearchText,
			filteredStocks,
			onStockSelectedFromAutocomplete,
			saveSpecialPrice,
			resetForm,
			closeModal,
		}
	},
}
</script>

<style lang="scss" scoped>
// Import typography variables from parent modal
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

$font-family-base: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
	Roboto, 'Helvetica Neue', Arial, sans-serif;
$font-weight-normal: 400;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 700;

.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.75);
	backdrop-filter: blur(4px);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 12000; // Higher than parent modal
	padding: 20px;
}

.modal {
	position: relative;
	background: white;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	max-width: 800px;
	width: 100%;
	max-height: 80vh;
	border-radius: 12px;
	margin: auto;
	font-family: $font-family-base;
}

.modal-header {
	background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
	color: white;
	padding: 28px 35px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: none;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	position: relative;
	flex-shrink: 0;
	border-radius: 12px 12px 0 0;

	.modal-header-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.modal-title-section {
		.modal-main-title {
			font-size: 1.5rem;
			font-weight: $font-weight-bold;
			margin: 0;
			color: white;
			letter-spacing: -0.025em;
			line-height: 1.2;
		}
	}

	.modal-customer-info {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 0.875rem;
		color: rgba(255, 255, 255, 0.95);
		line-height: 1.5;

		.customer-id {
			font-weight: $font-weight-semibold;
			font-family: 'SF Mono', Monaco, 'Courier New', monospace;
			font-size: 0.8125rem;
			background: rgba(255, 255, 255, 0.15);
			padding: 2px 8px;
			border-radius: 4px;
		}

		.separator {
			color: rgba(255, 255, 255, 0.4);
		}

		.customer-name {
			font-weight: $font-weight-medium;
		}

		.customer-nickname {
			font-style: normal;
			color: rgba(255, 255, 255, 0.85);
			font-size: 0.8125rem;
		}
	}
}

.modal-close-absolute {
	position: absolute;
	top: 30px;
	right: 30px;
	background: #dc3545;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 10px 20px;
	font-family: $font-family-base;
	font-size: 0.9375rem;
	font-weight: $font-weight-semibold;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	z-index: 10;
	transition: all 0.2s ease;
	box-shadow: 0 4px 12px rgba(220, 53, 69, 0.25);

	&:hover {
		background: #c82333;
		transform: translateY(-2px);
		box-shadow: 0 6px 16px rgba(220, 53, 69, 0.35);
	}
}

.modal-body {
	padding: 30px 35px;
	overflow-y: auto;
	flex-grow: 1;
	background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);

	.message {
		margin-bottom: 20px;
		border-radius: 8px;
	}
}

.form-container {
	.form-group {
		margin-bottom: 24px;

		.form-label {
			display: block;
			margin-bottom: 8px;
			font-size: 0.9375rem;
			font-weight: $font-weight-semibold;
			color: #495057;
			letter-spacing: -0.01em;
		}

		.current-price-display {
			padding: 12px 16px;
			background: #e9ecef;
			border-radius: 6px;
			font-size: 1.125rem;
			font-weight: $font-weight-semibold;
			color: #2d5a87;
			font-variant-numeric: tabular-nums;
			display: flex;
			align-items: center;
			gap: 12px;

			.price-label {
				font-size: 0.875rem;
				color: #6c757d;
				font-weight: $font-weight-medium;
				background: rgba(255, 255, 255, 0.7);
				padding: 2px 8px;
				border-radius: 4px;
			}
		}
	}

	// Buefy autocomplete dropdown width overrides
	::v-deep .autocomplete {
		.dropdown-menu {
			min-width: 600px !important;
			width: auto !important;
		}

		.dropdown-content {
			max-width: none !important;
			min-width: 600px !important;
			width: auto !important;
		}

		.dropdown-item {
			white-space: nowrap !important;
			overflow: visible !important;
			text-overflow: initial !important;
		}
	}

	// Additional dropdown positioning
	::v-deep .dropdown .dropdown-menu {
		min-width: 600px !important;
	}

	// Autocomplete styling
	.stock-option {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		white-space: nowrap;
		min-width: 580px;

		.stock-id {
			font-family: 'SF Mono', Monaco, 'Courier New', monospace;
			font-size: 0.875rem;
			color: #6c757d;
			font-weight: $font-weight-medium;
		}

		.stock-separator {
			color: #dee2e6;
		}

		.stock-name {
			flex: 1;
			color: #212529;
			min-width: 200px;
		}

		.stock-price {
			font-weight: $font-weight-semibold;
			color: #2d5a87;
			font-variant-numeric: tabular-nums;
			margin-left: auto;
			padding-left: 16px;
		}
	}

	.no-results {
		display: block;
		padding: 8px 12px;
		color: #6c757d;
		font-style: italic;
	}

	.form-actions {
		display: flex;
		gap: 12px;
		margin-top: 32px;
		padding-top: 24px;
		border-top: 1px solid #dee2e6;

		.btn-save,
		.btn-cancel {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			padding: 12px 24px;
			border: none;
			border-radius: 6px;
			font-family: $font-family-base;
			font-size: 0.9375rem;
			font-weight: $font-weight-semibold;
			cursor: pointer;
			transition: all 0.2s ease;

			svg {
				width: 16px;
				height: 16px;
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}

		.btn-save {
			background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
			color: white;

			&:hover:not(:disabled) {
				transform: translateY(-2px);
				box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
			}
		}

		.btn-cancel {
			background: white;
			color: #6c757d;
			border: 1px solid #dee2e6;

			&:hover {
				background: #f8f9fa;
				transform: translateY(-1px);
			}
		}
	}
}

.modal-fade-enter,
.modal-fade-leave-to {
	opacity: 0;
	transform: scale(0.95);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: all 0.3s ease;
}

.modal-fade-enter-to,
.modal-fade-leave {
	opacity: 1;
	transform: scale(1);
}
</style>
